import _ from "lodash";

/**
 * Bounty stage model: round 0 is synced known; later rounds re-seed the predicted winners
 * (worse half anchors and picks its opponent from the better half). Picks key on (round, anchor)
 * so an upstream change can't corrupt downstream ones.
 * Mirrors Pickstop.Mobile/lib/services/bounty_bracket_state.dart.
 */
export class BountyModel {
    /**
     * @param {object} ctx
     * @param {any} ctx.currentStage format stage (type 4) incl. bracket + bountyStageDetails
     * @param {any[]} ctx.teams event teams (with seed for this stage)
     * @param {any[]} ctx.officialPicks official MatchPicks (all stages)
     * @param {any[]} ctx.userPicks user MatchPicks (all stages)
     * @param {number} ctx.eventId
     * @param {number} ctx.eventStageId
     * @param {number} ctx.eventSubstageId
     */
    constructor({
        currentStage,
        teams,
        officialPicks,
        userPicks,
        eventId,
        eventStageId,
        eventSubstageId,
    }) {
        this.eventId = eventId;
        this.eventStageId = eventStageId;
        this.eventSubstageId = eventSubstageId;

        const parts = currentStage?.bracket?.freeformParts ?? [];
        this.parts = parts;
        this.partIds = parts.map((p) => p.identifier);
        this.roundNames = parts.map(
            (p, i) => p.name ?? p.columns?.[0]?.name ?? `Round ${i + 1}`
        );
        this.bestOfPerRound = parts.map((p) => p.columns?.[0]?.bestOf ?? 1);
        this.teamCount = currentStage?.bountyStageDetails?.teamCount ?? 0;
        this.useSeriesScore =
            currentStage?.bountyStageDetails?.useSeriesScore ?? false;

        this.seeds = {};
        for (const team of teams ?? []) {
            if (team.eventStageId === eventStageId && team.seed != null) {
                this.seeds[team.id] = team.seed;
            }
        }

        const officialForStage = (officialPicks ?? []).filter(
            (p) => p.eventStageId === eventStageId
        );
        this.officialAll = officialForStage;
        this.firstRound = officialForStage
            .filter((p) => p.bracketPart === this.partIds[0])
            .sort((a, b) => a.bracketColIndex - b.bracketColIndex);

        this.officialPairs = {};
        this.officialWinners = {};
        for (const pick of officialForStage) {
            if (pick.topTeamId != null && pick.bottomTeamId != null) {
                if (!this.officialPairs[pick.bracketPart]) {
                    this.officialPairs[pick.bracketPart] = new Set();
                }
                this.officialPairs[pick.bracketPart].add(
                    pairKey(pick.topTeamId, pick.bottomTeamId)
                );
            }
            if (pick.winnerTeamId != null) {
                if (!this.officialWinners[pick.bracketPart]) {
                    this.officialWinners[pick.bracketPart] = new Set();
                }
                this.officialWinners[pick.bracketPart].add(pick.winnerTeamId);
            }
        }

        // Picks keyed `${round}:${keyTeamId}` (round 0 keys by official top team).
        this.opponentByKey = {};
        this.winnerByKey = {};
        this.scoreByKey = {};
        this._loadUserPicks(userPicks ?? []);
    }

    get roundCount() {
        return this.partIds.length;
    }

    gamesInRound(round) {
        return Math.floor(this.teamCount / Math.pow(2, round + 1));
    }

    /**
     * @returns {Array<{gameIndex:number, colIndex:number, topTeamId:?number,
     *   bottomTeamId:?number, winnerTeamId:?number, winnerScore:?number,
     *   loserScore:?number}>}
     */
    gamesForRound(round) {
        const count = this.gamesInRound(round);
        if (round === 0) {
            return _.range(count).map((i) => this._firstRoundGame(i));
        }

        const halves = this._halvesForRound(round);
        return _.range(count).map((i) => {
            const anchor = halves?.anchors[i] ?? null;
            const opponent =
                anchor == null
                    ? null
                    : this._effectiveOpponent(round, anchor, halves);
            let winner = null;
            let score = null;
            if (anchor != null) {
                const stored = this.winnerByKey[`${round}:${anchor}`];
                if (stored != null && (stored === anchor || stored === opponent)) {
                    winner = stored;
                    score = this.scoreByKey[`${round}:${anchor}`] ?? null;
                }
            }
            return {
                gameIndex: i,
                colIndex: i,
                topTeamId: anchor,
                bottomTeamId: opponent,
                winnerTeamId: winner,
                winnerScore: score?.[0] ?? null,
                loserScore: score?.[1] ?? null,
            };
        });
    }

    /** Opponents the game's anchor can face; null while underivable. */
    eligibleOpponents(round, gameIndex) {
        const halves = this._halvesForRound(round);
        if (!halves) {
            return null;
        }
        const anchor = halves.anchors[gameIndex];
        const takenByOthers = new Set();
        for (const other of halves.anchors) {
            if (other === anchor) {
                continue;
            }
            const assigned = this.opponentByKey[`${round}:${other}`];
            if (assigned != null && halves.pickees.includes(assigned)) {
                takenByOthers.add(assigned);
            }
        }
        return halves.pickees.filter((t) => !takenByOthers.has(t));
    }

    pickOpponent(round, gameIndex, teamId) {
        const halves = this._halvesForRound(round);
        if (!halves || !halves.pickees.includes(teamId)) {
            return;
        }
        const anchor = halves.anchors[gameIndex];

        // Steal from any other anchor holding this opponent.
        for (const other of halves.anchors) {
            if (other === anchor) {
                continue;
            }
            if (this.opponentByKey[`${round}:${other}`] === teamId) {
                delete this.opponentByKey[`${round}:${other}`];
                delete this.winnerByKey[`${round}:${other}`];
                delete this.scoreByKey[`${round}:${other}`];
            }
        }

        const key = `${round}:${anchor}`;
        if (this.opponentByKey[key] !== teamId) {
            this.opponentByKey[key] = teamId;
            delete this.winnerByKey[key];
            delete this.scoreByKey[key];
        }
    }

    pickWinner(round, gameIndex, winnerId, winnerScore, loserScore) {
        const game = this.gamesForRound(round)[gameIndex];
        if (!game) {
            return;
        }
        if (winnerId !== game.topTeamId && winnerId !== game.bottomTeamId) {
            return;
        }
        const key = `${round}:${game.topTeamId}`;
        this.winnerByKey[key] = winnerId;
        if (winnerScore != null) {
            this.scoreByKey[key] = [winnerScore, loserScore ?? 0];
        } else {
            delete this.scoreByKey[key];
        }
    }

    isComplete() {
        for (let r = 0; r < this.roundCount; r++) {
            const games = this.gamesForRound(r);
            if (games.length !== this.gamesInRound(r)) {
                return false;
            }
            for (const game of games) {
                if (
                    game.topTeamId == null ||
                    game.bottomTeamId == null ||
                    game.winnerTeamId == null
                ) {
                    return false;
                }
                if (
                    this.useSeriesScore &&
                    (this.bestOfPerRound[r] ?? 1) > 1 &&
                    game.winnerScore == null
                ) {
                    return false;
                }
            }
        }
        return true;
    }

    pairingCorrect(round, game) {
        if (round === 0 || game.topTeamId == null || game.bottomTeamId == null) {
            return false;
        }
        const pairs = this.officialPairs[this.partIds[round]];
        return (
            !!pairs && pairs.has(pairKey(game.topTeamId, game.bottomTeamId))
        );
    }

    winnerCorrect(round, game) {
        if (game.winnerTeamId == null) {
            return false;
        }
        const winners = this.officialWinners[this.partIds[round]];
        return !!winners && winners.has(game.winnerTeamId);
    }

    /** All decided games as MatchPick objects (this stage only). */
    serializePicks() {
        const picks = [];
        for (let r = 0; r < this.roundCount; r++) {
            for (const game of this.gamesForRound(r)) {
                const { topTeamId, bottomTeamId, winnerTeamId } = game;
                if (
                    topTeamId == null ||
                    bottomTeamId == null ||
                    winnerTeamId == null
                ) {
                    continue;
                }
                const winnerScore = game.winnerScore ?? 1;
                const loserScore = game.winnerScore != null ? game.loserScore : 0;
                picks.push({
                    eventId: this.eventId,
                    eventStageId: this.eventStageId,
                    eventSubstageId: this.eventSubstageId,
                    bracketPart: this.partIds[r],
                    bracketCol: 0,
                    bracketColIndex: game.colIndex,
                    topTeamId,
                    bottomTeamId,
                    winnerTeamId,
                    loserTeamId:
                        winnerTeamId === topTeamId ? bottomTeamId : topTeamId,
                    topTeamScore:
                        winnerTeamId === topTeamId ? winnerScore : loserScore,
                    bottomTeamScore:
                        winnerTeamId === bottomTeamId ? winnerScore : loserScore,
                });
            }
        }
        return picks;
    }

    _loadUserPicks(userPicks) {
        for (const pick of userPicks) {
            if (pick.eventStageId !== this.eventStageId) {
                continue;
            }
            const round = this.partIds.indexOf(pick.bracketPart);
            if (round < 0) {
                continue;
            }
            const { topTeamId, bottomTeamId, winnerTeamId } = pick;
            if (
                topTeamId == null ||
                bottomTeamId == null ||
                winnerTeamId == null
            ) {
                continue;
            }

            let key;
            if (round === 0) {
                const game = this.firstRound.find(
                    (g) =>
                        (g.topTeamId === topTeamId &&
                            g.bottomTeamId === bottomTeamId) ||
                        (g.topTeamId === bottomTeamId &&
                            g.bottomTeamId === topTeamId)
                );
                if (!game) {
                    continue;
                }
                key = `0:${game.topTeamId}`;
            } else {
                key = `${round}:${topTeamId}`;
                this.opponentByKey[key] = bottomTeamId;
            }
            this.winnerByKey[key] = winnerTeamId;

            const topScore = pick.topTeamScore ?? 0;
            const bottomScore = pick.bottomTeamScore ?? 0;
            // A real series score has a winning side above 1; 1/0 is the default.
            if (topScore > 1 || bottomScore > 1) {
                const winnerScore =
                    winnerTeamId === topTeamId ? topScore : bottomScore;
                const loserScore =
                    winnerTeamId === topTeamId ? bottomScore : topScore;
                this.scoreByKey[key] = [winnerScore, loserScore];
            }
        }
    }

    _firstRoundGame(i) {
        if (i >= this.firstRound.length) {
            return {
                gameIndex: i,
                colIndex: i,
                topTeamId: null,
                bottomTeamId: null,
                winnerTeamId: null,
                winnerScore: null,
                loserScore: null,
            };
        }
        const official = this.firstRound[i];
        const top = official.topTeamId ?? null;
        const bottom = official.bottomTeamId ?? null;
        let winner = null;
        let score = null;
        if (top != null) {
            const stored = this.winnerByKey[`0:${top}`];
            if (stored != null && (stored === top || stored === bottom)) {
                winner = stored;
                score = this.scoreByKey[`0:${top}`] ?? null;
            }
        }
        return {
            gameIndex: i,
            colIndex: official.bracketColIndex,
            topTeamId: top,
            bottomTeamId: bottom,
            winnerTeamId: winner,
            winnerScore: score?.[0] ?? null,
            loserScore: score?.[1] ?? null,
        };
    }

    /** Anchors (worse half) and pickees (better half) entering a round. */
    _halvesForRound(round, gamesFor = (r) => this.gamesForRound(r)) {
        if (round === 0) {
            return null;
        }
        const ranked = this._rankedWinners(round - 1, gamesFor);
        if (!ranked) {
            return null;
        }
        const half = ranked.length / 2;
        return { anchors: ranked.slice(half), pickees: ranked.slice(0, half) };
    }

    /** Winners of a round ranked by the rank of the opponent they beat. */
    _rankedWinners(round, gamesFor = (r) => this.gamesForRound(r)) {
        const ranks = this._ranksEnteringRound(round, gamesFor);
        if (!ranks) {
            return null;
        }
        const games = gamesFor(round);
        if (games.length !== this.gamesInRound(round)) {
            return null;
        }
        const entries = [];
        for (const game of games) {
            const { topTeamId, bottomTeamId, winnerTeamId } = game;
            if (
                topTeamId == null ||
                bottomTeamId == null ||
                winnerTeamId == null
            ) {
                return null;
            }
            const beaten =
                winnerTeamId === topTeamId ? bottomTeamId : topTeamId;
            const beatenRank = ranks[beaten];
            if (beatenRank == null) {
                return null;
            }
            entries.push([winnerTeamId, beatenRank]);
        }
        entries.sort((a, b) => a[1] - b[1]);
        return entries.map((e) => e[0]);
    }

    _ranksEnteringRound(round, gamesFor = (r) => this.gamesForRound(r)) {
        if (round === 0) {
            return Object.keys(this.seeds).length > 0 ? this.seeds : null;
        }
        const ranked = this._rankedWinners(round - 1, gamesFor);
        if (!ranked) {
            return null;
        }
        const ranks = {};
        ranked.forEach((teamId, i) => {
            ranks[teamId] = i + 1;
        });
        return ranks;
    }

    _effectiveOpponent(round, anchor, halves) {
        const assigned = this.opponentByKey[`${round}:${anchor}`];
        if (assigned != null && halves.pickees.includes(assigned)) {
            return assigned;
        }

        // Forced when this is the only open game and one opponent remains.
        const takenValid = new Set();
        let openGames = 0;
        for (const other of halves.anchors) {
            const otherAssigned = this.opponentByKey[`${round}:${other}`];
            if (otherAssigned != null && halves.pickees.includes(otherAssigned)) {
                takenValid.add(otherAssigned);
            } else {
                openGames++;
            }
        }
        const remaining = halves.pickees.filter((t) => !takenValid.has(t));
        if (openGames === 1 && remaining.length === 1) {
            return remaining[0];
        }
        return null;
    }

    /** Official picks of a round in game shape, in the raw synced order/orientation. */
    _officialGamesRaw(round) {
        const partId = this.partIds[round];
        const picks = this.officialAll
            .filter((p) => p.bracketPart === partId)
            .sort((a, b) => a.bracketColIndex - b.bracketColIndex);
        return picks.map((p, i) => ({
            gameIndex: i,
            colIndex: p.bracketColIndex,
            topTeamId: p.topTeamId ?? null,
            bottomTeamId: p.bottomTeamId ?? null,
            winnerTeamId: p.winnerTeamId ?? null,
            winnerScore:
                p.winnerTeamId != null
                    ? p.winnerTeamId === p.topTeamId
                        ? p.topTeamScore
                        : p.bottomTeamScore
                    : null,
            loserScore:
                p.winnerTeamId != null
                    ? p.winnerTeamId === p.topTeamId
                        ? p.bottomTeamScore
                        : p.topTeamScore
                    : null,
        }));
    }

    /**
     * Official results laid out like the user view (re-seed: anchor on top,
     * ordered by anchor rank) so switching views keeps games in the same slots.
     */
    officialGamesForRound(round) {
        const raw = this._officialGamesRaw(round);
        if (round === 0) {
            return raw;
        }
        const halves = this._halvesForRound(round, (r) =>
            this._officialGamesRaw(r)
        );
        if (!halves) {
            return raw;
        }
        return _.range(this.gamesInRound(round)).map((i) => {
            const anchor = halves.anchors[i] ?? null;
            const game =
                anchor == null
                    ? null
                    : raw.find(
                          (g) =>
                              g.topTeamId === anchor || g.bottomTeamId === anchor
                      );
            if (!game) {
                return {
                    gameIndex: i,
                    colIndex: i,
                    topTeamId: anchor,
                    bottomTeamId: null,
                    winnerTeamId: null,
                    winnerScore: null,
                    loserScore: null,
                };
            }
            const opponent =
                game.topTeamId === anchor ? game.bottomTeamId : game.topTeamId;
            return {
                gameIndex: i,
                colIndex: i,
                topTeamId: anchor,
                bottomTeamId: opponent,
                winnerTeamId: game.winnerTeamId,
                winnerScore: game.winnerScore,
                loserScore: game.loserScore,
            };
        });
    }
}

function pairKey(a, b) {
    return a < b ? `${a}-${b}` : `${b}-${a}`;
}

/** Constructor context for a BountyModel; the ctor tolerates null pick lists. */
export function bountyModelContext(
    eventData,
    currentStage,
    currentEventStage,
    officialPicks,
    userPicks
) {
    return {
        currentStage,
        teams: eventData.teams,
        officialPicks,
        userPicks,
        eventId: eventData.id,
        eventStageId: currentEventStage.id,
        eventSubstageId: currentEventStage.eventSubstage[0]?.id,
    };
}

/**
 * Fills a copy of the generated freeformParts with a bounty model's games so
 * the standard freeform renderer can display them.
 *
 * @param {BountyModel} model
 * @param {any[]} teams
 * @param {{official?: boolean, withCues?: boolean}} options official = fill
 *   from official picks instead of the user's model; withCues = apply
 *   set-based correctness cues.
 */
export function fillBountyFreeformParts(model, teams, options = {}) {
    const parts = _.cloneDeep(model.parts);

    for (let r = 0; r < model.roundCount; r++) {
        const column = parts[r]?.columns?.[0];
        if (!column) {
            continue;
        }

        const games = options.official
            ? model.officialGamesForRound(r)
            : model.gamesForRound(r);

        for (const game of games) {
            const bracketMatch = column.matches.find(
                (m) => m.colIndex === game.colIndex
            );
            if (!bracketMatch) {
                continue;
            }

            const topTeam = teams.find((t) => t.id === game.topTeamId);
            const botTeam = teams.find((t) => t.id === game.bottomTeamId);
            const started = game.winnerScore != null;
            const winnerCorrect =
                options.withCues && model.winnerCorrect(r, game);

            bracketMatch.pairingCorrect =
                options.withCues && model.pairingCorrect(r, game);
            bracketMatch.participants = [
                {
                    id: topTeam?.id ?? "",
                    resultText: started
                        ? `${
                              game.winnerTeamId === game.topTeamId
                                  ? game.winnerScore
                                  : game.loserScore
                          }`
                        : "",
                    isWinner: game.winnerTeamId === topTeam?.id,
                    status: "PLAYED",
                    name: topTeam?.abbreviation ?? "",
                    logoUrl: topTeam?.logoUrl,
                    correctParticipant:
                        winnerCorrect && game.winnerTeamId === topTeam?.id,
                    correctPlacement: false,
                },
                {
                    id: botTeam?.id ?? "",
                    resultText: started
                        ? `${
                              game.winnerTeamId === game.bottomTeamId
                                  ? game.winnerScore
                                  : game.loserScore
                          }`
                        : "",
                    isWinner: game.winnerTeamId === botTeam?.id,
                    status: "PLAYED",
                    name: botTeam?.abbreviation ?? "",
                    logoUrl: botTeam?.logoUrl,
                    correctParticipant:
                        winnerCorrect && game.winnerTeamId === botTeam?.id,
                    correctPlacement: false,
                },
            ];
        }
    }

    return parts;
}

